import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { featureFlagsApi } from './api';
import { useFeatureFlagsStore } from './store';
import type { FeatureFlag } from './types';

// Query keys
export const featureFlagsKeys = {
  all: ['feature-flags'] as const,
  list: () => [...featureFlagsKeys.all, 'list'] as const,
  detail: (key: string) => [...featureFlagsKeys.all, 'detail', key] as const,
};

// Cache duration: 5 minutes
const STALE_TIME = 5 * 60 * 1000;

/**
 * Hook to fetch and cache all feature flags
 * Automatically syncs with the Zustand store
 */
export function useFeatureFlags() {
  const setFlags = useFeatureFlagsStore((state) => state.setFlags);

  // Track last synced timestamp to prevent infinite loops
  const lastSyncedAtRef = useRef<number>(0);

  const query = useQuery({
    queryKey: featureFlagsKeys.list(),
    queryFn: featureFlagsApi.getAll,
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Store query in ref to avoid dependency on query.data object
  const queryRef = useRef(query);
  queryRef.current = query;

  // Sync with store when data changes - only depend on dataUpdatedAt (stable timestamp)
  useEffect(() => {
    const { data, dataUpdatedAt } = queryRef.current;
    if (data && Array.isArray(data) && dataUpdatedAt > lastSyncedAtRef.current) {
      lastSyncedAtRef.current = dataUpdatedAt;
      setFlags(data);
    }
  }, [query.dataUpdatedAt, setFlags]);

  return query;
}

/**
 * Hook to check if a specific feature is enabled
 * Returns { isEnabled, isLoading, flag }
 */
export function useIsFeatureEnabled(key: string): {
  isEnabled: boolean;
  isLoading: boolean;
  flag: FeatureFlag | undefined;
} {
  const { data: flags, isLoading } = useFeatureFlags();
  const isEnabledFn = useFeatureFlagsStore((state) => state.isEnabled);
  const getFlag = useFeatureFlagsStore((state) => state.getFlag);

  const result = useMemo(() => {
    if (!flags) {
      return { isEnabled: false, flag: undefined };
    }
    return {
      isEnabled: isEnabledFn(key),
      flag: getFlag(key),
    };
  }, [flags, key, isEnabledFn, getFlag]);

  return {
    ...result,
    isLoading,
  };
}

/**
 * Hook to get multiple feature flags at once
 * Returns a map of key -> isEnabled
 */
export function useFeatureFlagsMap(keys: string[]): {
  flags: Record<string, boolean>;
  isLoading: boolean;
} {
  const { isLoading } = useFeatureFlags();
  const isEnabledFn = useFeatureFlagsStore((state) => state.isEnabled);
  const storeFlags = useFeatureFlagsStore((state) => state.flags);

  const flags = useMemo(() => {
    const result: Record<string, boolean> = {};
    keys.forEach((key) => {
      result[key] = isEnabledFn(key);
    });
    return result;
  }, [keys, isEnabledFn, storeFlags]);

  return { flags, isLoading };
}

/**
 * Hook to get flags by category
 */
export function useFeatureFlagsByCategory(categoryKey: string) {
  const { data, isLoading } = useFeatureFlags();
  const getFlagsByCategory = useFeatureFlagsStore(
    (state) => state.getFlagsByCategory
  );

  const flags = useMemo(() => {
    if (!data) return [];
    return getFlagsByCategory(categoryKey);
  }, [data, categoryKey, getFlagsByCategory]);

  return { flags, isLoading };
}
